import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { PlainText, SourceMaterialsMediaObject, Website } from 'types'
import { AuthorizationError } from 'utils/error'

export type SourceType = 'Website' | 'PlainText' | 'SourceMaterialsMediaObject'

type SourceTypeMap = {
  Website: Website
  PlainText: PlainText
  SourceMaterialsMediaObject: SourceMaterialsMediaObject
}

export interface Data<T extends SourceType = SourceType> {
  source: SourceTypeMap[T]
}

type VariableMap = {
  Website: Pick<Website, 'url' | 'type'> & { scrape_images?: boolean }
  PlainText: Pick<PlainText, 'text' | 'type'>
  SourceMaterialsMediaObject: Pick<SourceMaterialsMediaObject, 'type'> & { media_object_id: number }
}

export type Variables = {
  [K in keyof VariableMap]: VariableMap[K] & { signal?: AbortSignal }
}[keyof VariableMap]

export type Options = Omit<UseMutationOptions<Data, Error, Variables>, 'mutationKey' | 'mutationFn'>

const createSource = async <T extends Variables>({
  workspaceId,
  signal,
  ...variables
}: Variables & { workspaceId: number }) => {
  const res = await sendToApi<Data<T['type']>>(`/w/${workspaceId}/sources`, {
    method: 'POST',
    body: variables,
    signal,
  })

  if (res.type === 'exception' && res.data instanceof DOMException && res.data.name === 'AbortError') {
    throw res.data
  }

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.data === 'cancel') {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }

    if (res.type === 'error' && res.status === 403) {
      throw new AuthorizationError()
    }

    const message = typeof res.data === 'string' ? res.data : JSON.stringify(res.data)
    throw new Error(message)
  }

  return res.data
}

export const useCreateSource = (options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()

  const mutation = useMutation<Data, Error, Variables>(
    async (vars) =>
      createSource({
        workspaceId,
        ...vars,
      }),
    options,
  )

  const mutateAsync = <T extends Variables>(vars: T, options?: Options) =>
    mutation.mutateAsync(vars, options) as Promise<Data<T['type']>>

  return {
    ...mutation,
    mutateAsync,
  }
}
